import { useMemo, useState, useRef, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { Search, Wrench, BookOpen, X } from "lucide-react";
import { tools } from "@/lib/tools";
import { ARTIGOS_LIST_LIGHT } from "@/lib/artigos-list-light";

type Result = {
  kind: "tool" | "artigo";
  slug: string;
  title: string;
  desc: string;
};

const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export function SiteSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const index = useMemo<Result[]>(() => {
    const t: Result[] = tools.map((x) => ({
      kind: "tool",
      slug: x.slug,
      title: x.title,
      desc: x.short,
    }));
    const a: Result[] = ARTIGOS_LIST_LIGHT.map((x) => ({
      kind: "artigo",
      slug: x.slug,
      title: x.titulo,
      desc: x.resumo,
    }));
    return [...t, ...a];
  }, []);

  const results = useMemo(() => {
    const term = normalize(q.trim());
    if (term.length < 2) return [] as Result[];
    const tokens = term.split(/\s+/).filter(Boolean);
    return index
      .map((r) => {
        const hay = normalize(`${r.title} ${r.desc}`);
        const score = tokens.reduce((acc, tk) => acc + (hay.includes(tk) ? 1 : 0), 0);
        return { r, score };
      })
      .filter((x) => x.score === tokens.length)
      .slice(0, 8)
      .map((x) => x.r);
  }, [q, index]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={wrapRef} className="relative w-full max-w-2xl">
      <label htmlFor="site-search" className="sr-only">
        Buscar ferramentas e artigos
      </label>
      <div className="relative">
        <Search
          className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground"
          aria-hidden
        />
        <input
          id="site-search"
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Buscar ferramentas, artigos, calculadoras..."
          className="w-full rounded-full border border-white/30 bg-white/95 pl-12 pr-12 py-4 text-[15px] sm:text-base text-foreground placeholder:text-muted-foreground shadow-elevated focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
          autoComplete="off"
        />
        {q && (
          <button
            type="button"
            onClick={() => {
              setQ("");
              setOpen(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-muted text-muted-foreground"
            aria-label="Limpar busca"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div className="absolute z-50 mt-2 w-full rounded-2xl border border-border bg-card shadow-elevated overflow-hidden text-left">
          {results.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">
              Nada encontrado para "{q}". Tente outro termo.
            </div>
          ) : (
            <ul className="max-h-[60vh] overflow-y-auto divide-y divide-border">
              {results.map((r) => (
                <li key={`${r.kind}-${r.slug}`}>
                  <Link
                    to={r.kind === "tool" ? "/ferramentas/$slug" : "/artigos/$slug"}
                    params={{ slug: r.slug }}
                    onClick={() => setOpen(false)}
                    className="flex items-start gap-3 p-3 sm:p-4 hover:bg-surface/60 transition-colors"
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                        r.kind === "tool"
                          ? "bg-primary/10 text-primary"
                          : "bg-accent/15 text-accent-foreground"
                      }`}
                    >
                      {r.kind === "tool" ? (
                        <Wrench className="h-4 w-4" />
                      ) : (
                        <BookOpen className="h-4 w-4" />
                      )}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                          {r.kind === "tool" ? "Ferramenta" : "Artigo"}
                        </span>
                      </div>
                      <div className="font-semibold text-sm text-foreground truncate">
                        {r.title}
                      </div>
                      <div className="text-xs text-muted-foreground line-clamp-1">{r.desc}</div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
